import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { useUpload } from "../hooks/useUpload";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { useJobTypeForms } from "@/features/admin/form-config/forms/useJobTypeForms";
import "./UploadDialog.css";

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
}

// Folder-select keeps only document files + the conventional manifest; stray
// files (e.g. .DS_Store) are dropped so they don't 400 the batch upload.
const ALLOWED_DOC_EXT = new Set(["pdf", "png", "jpg", "jpeg", "tif", "tiff", "bmp", "webp"]);

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function UploadDialog({ open, onClose }: UploadDialogProps) {
  const navigate = useNavigate();
  const { items: jobTypes } = useJobTypes();
  const {
    files,
    addFiles,
    removeFile,
    clearFiles,
    upload,
    isUploading,
    progress,
    error,
  } = useUpload();

  const [batchName, setBatchName] = useState("");
  const [nameManuallyCleared, setNameManuallyCleared] = useState(false);
  const [jobTypeId, setJobTypeId] = useState("");
  // F-043 — declared form for source_declared job types (whitemail/correspondence).
  const [declaredFormId, setDeclaredFormId] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const addFolderFiles = useCallback(
    (list: FileList) => {
      const keep = Array.from(list).filter((f) => {
        const base = f.name.toLowerCase();
        if (base === "manifest.xml" || base === "manifest.json") return true;
        const ext = base.split(".").pop() ?? "";
        return ALLOWED_DOC_EXT.has(ext);
      });
      if (keep.length > 0) addFiles(keep);
    },
    [addFiles],
  );

  // Auto-generate batch name from first file (only if user hasn't cleared it)
  useEffect(() => {
    if (files.length > 0 && !batchName && !nameManuallyCleared) {
      const firstName = files[0]!.file.name.replace(/\.[^.]+$/, "");
      setBatchName(
        files.length === 1
          ? firstName
          : `${firstName} +${files.length - 1} more`,
      );
    }
  }, [files, batchName, nameManuallyCleared]);

  // Only active JobTypes that have a published form version can accept an
  // upload. Filtering here — and on the picker below — keeps the auto-select
  // behavior from picking a draft-only JobType that the upload route would
  // reject.
  const selectableJobTypes = (jobTypes ?? []).filter(
    (jt) => jt.isActive && jt.currentPublishedVersionId,
  );

  // Auto-select first job type if only one is selectable
  useEffect(() => {
    if (!jobTypeId && selectableJobTypes.length === 1) {
      setJobTypeId(selectableJobTypes[0]!.id);
    }
  }, [selectableJobTypes, jobTypeId]);

  // F-043 — a source_declared job declares the form for the whole batch
  // (whitemail/correspondence) instead of fingerprinting. Offer a form picker
  // so manual uploads can use that mode; the backend already accepts
  // `declaredFormId` but the dialog never sent it.
  const { forms } = useJobTypeForms(jobTypeId);
  const selectedJobType = selectableJobTypes.find((jt) => jt.id === jobTypeId);
  const isSourceDeclared = selectedJobType?.classificationMode === "source_declared";

  // Reset the declared form whenever the job type changes (forms differ per job).
  useEffect(() => {
    setDeclaredFormId("");
  }, [jobTypeId]);

  // Default the declared form to the job's default form once forms load.
  useEffect(() => {
    if (isSourceDeclared && forms.length > 0 && !declaredFormId) {
      setDeclaredFormId(forms.find((f) => f.isDefault)?.id ?? forms[0]!.id);
    }
  }, [isSourceDeclared, forms, declaredFormId]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !isUploading) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isUploading, onClose]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      clearFiles();
      setBatchName("");
      setNameManuallyCleared(false);
      setJobTypeId("");
      setDeclaredFormId("");
    }
  }, [open, clearFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
        e.target.value = "";
      }
    },
    [addFiles],
  );

  const handleSubmit = useCallback(async () => {
    if (!batchName.trim() || !jobTypeId || files.length === 0) return;

    try {
      const result = await upload(
        batchName.trim(),
        jobTypeId,
        isSourceDeclared ? declaredFormId || undefined : undefined,
      );
      onClose();
      navigate(`/batches/${result.batchId}`);
    } catch {
      // Error is set in the hook
    }
  }, [batchName, jobTypeId, files.length, upload, onClose, navigate, isSourceDeclared, declaredFormId]);

  if (!open) return null;

  const canSubmit =
    files.length > 0 &&
    batchName.trim().length > 0 &&
    jobTypeId.length > 0 &&
    !isUploading;

  return (
    <div
      className="upload-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isUploading) onClose();
      }}
    >
      <div className="upload-dialog" role="dialog" aria-modal="true" aria-label="Upload batch">
        {/* Header */}
        <div className="upload-dialog-header">
          <h2 className="upload-dialog-title">Upload Batch</h2>
          <button
            className="upload-dialog-close"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="upload-dialog-body">
          {/* Drop zone */}
          <div
            className={`upload-dropzone ${dragActive ? "upload-dropzone--active" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <div className="upload-dropzone-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="upload-dropzone-text">
              Drop files here or click to browse
            </div>
            <div className="upload-dropzone-hint">
              PDF, PNG, JPEG, TIFF, BMP, WebP -- max 50 MB per file
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
          </div>

          {/* Folder select affordance */}
          <div className="upload-folder-row">
            <span className="upload-folder-label">Or upload a folder with a manifest:</span>
            <button
              type="button"
              className="upload-btn-cancel"
              onClick={() => folderInputRef.current?.click()}
            >
              Select folder
            </button>
            <input
              ref={folderInputRef}
              type="file"
              multiple
              // @ts-expect-error webkitdirectory/directory are non-standard but supported in target browsers
              webkitdirectory=""
              directory=""
              data-testid="upload-folder-input"
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files) addFolderFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="upload-file-list">
              <div className="upload-file-count">
                {files.length} file{files.length !== 1 ? "s" : ""} selected
                <span className="upload-file-total-size">
                  ({formatFileSize(files.reduce((sum, f) => sum + f.file.size, 0))})
                </span>
              </div>
              {files.map((f) => (
                <div key={f.id} className="upload-file-item">
                  <span className="upload-file-name">{f.file.name}</span>
                  <span className="upload-file-size">
                    {formatFileSize(f.file.size)}
                  </span>
                  <button
                    className="upload-file-remove"
                    onClick={() => removeFile(f.id)}
                    disabled={isUploading}
                    aria-label={`Remove ${f.file.name}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Job type selector */}
          <div className="upload-field">
            <label className="upload-field-label" htmlFor="upload-job-type">
              Job Type
            </label>
            {selectableJobTypes.length === 0 ? (
              <div
                className="upload-field-empty"
                data-testid="upload-no-jobtypes"
              >
                <p>
                  No published job types yet. Upload requires a published
                  job type — drafts won't appear here.
                </p>
                <Link
                  to="/admin/job-types"
                  className="upload-field-empty-link"
                  data-testid="upload-jobtype-deep-link"
                >
                  Open Job Types →
                </Link>
              </div>
            ) : (
              <select
                id="upload-job-type"
                className="upload-field-select"
                value={jobTypeId}
                onChange={(e) => setJobTypeId(e.target.value)}
                disabled={isUploading}
              >
                <option value="">Select a job type...</option>
                {selectableJobTypes.map((jt) => (
                  <option key={jt.id} value={jt.id}>
                    {jt.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* F-043 — declared-form picker for source_declared job types. */}
          {isSourceDeclared && forms.length > 0 && (
            <div className="upload-field">
              <label className="upload-field-label" htmlFor="upload-declared-form">
                Form type
              </label>
              <select
                id="upload-declared-form"
                className="upload-field-select"
                value={declaredFormId}
                onChange={(e) => setDeclaredFormId(e.target.value)}
                disabled={isUploading}
              >
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.isDefault ? " (default)" : ""}
                  </option>
                ))}
              </select>
              <p className="upload-declared-form-hint">
                This job uses source-declared classification — every document in
                the batch is keyed as this form (no fingerprinting).
              </p>
            </div>
          )}

          {/* Batch name */}
          <div className="upload-field">
            <label className="upload-field-label" htmlFor="upload-batch-name">
              Batch Name
            </label>
            <div className="upload-field-with-clear">
              <input
                id="upload-batch-name"
                className="upload-field-input"
                type="text"
                value={batchName}
                onChange={(e) => {
                  setBatchName(e.target.value);
                  if (nameManuallyCleared && e.target.value) setNameManuallyCleared(false);
                }}
                placeholder="Auto-generated from filename"
                disabled={isUploading}
              />
              {batchName && !isUploading && (
                <button
                  className="upload-field-clear"
                  type="button"
                  onClick={() => { setBatchName(""); setNameManuallyCleared(true); }}
                  aria-label="Clear batch name"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Progress */}
          {isUploading && (
            <div className="upload-progress">
              <div className="upload-progress-bar">
                <div
                  className="upload-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="upload-progress-text">
                Uploading... {progress}%
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="upload-error" role="alert">
              <span className="upload-error-message">{error}</span>
              <button
                type="button"
                className="upload-error-retry"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                Retry upload
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="upload-dialog-footer">
          <button
            className="upload-btn-cancel"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            className="upload-btn-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isUploading ? "Uploading..." : `Upload ${files.length} File${files.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
